// Puente entre el auth de la app (useAuth) y el <PermissionsProvider> del kit (UI pura).
//
// El kit no conoce el catálogo de permisos: recibe el chequeo inyectado. Acá lo armamos desde
// useAuth, que ya expone hasPermission/hasScopeAccess + la lógica scopeCovers. NO se duplica
// lógica: este componente solo adapta y agrega el guard anti-flash de la carga inicial.
import { useMemo, type ReactNode } from 'react';
import { PermissionsProvider, type PermissionsContextValue } from '@/kit';
import { useAuth } from '../../hooks/useAuth';
import type { Permission, Scope } from '../../api/types/permissions';

export function PermissionsBridge({ children }: { children: ReactNode }) {
    const { hasPermission, hasScopeAccess, loading, user } = useAuth();

    const value = useMemo<PermissionsContextValue>(
        () => ({
            // Guard anti-flash: durante la carga INICIAL (loading + todavía sin user) denegamos para
            // no mostrar UI sensible. Un refresh en background (user ya presente) NO bloquea → el
            // menú no parpadea. (Antes vivía dentro del <Gate> local; ahora viaja por el provider.)
            hasPermission: (permission: string) => {
                if (loading && !user) return false;
                return hasPermission(permission as Permission);
            },
            hasScopeAccess: (scope: string, scopeRef?: string) => {
                if (loading && !user) return false;
                return hasScopeAccess(scope as Scope, scopeRef);
            },
        }),
        [hasPermission, hasScopeAccess, loading, user],
    );

    return <PermissionsProvider value={value}>{children}</PermissionsProvider>;
}
